import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    MessageSquare, RefreshCcw, Star, Sparkles, ShieldAlert,
    CheckCircle2, PauseCircle, Send, XCircle, Home, Info, X, ClipboardList,
} from "lucide-react";

/**
 * Admin · Content · Testimonials
 *
 * Full governance surface: verify, approve, publish, pause, withdraw,
 * feature, homepage-on/off, edit formatting only, associate feature
 * tags, view audit trail. Illustrative placeholders are visibly labelled
 * and cannot leak to production.
 */

const STATUS_TONE = {
    Submitted:         "bg-stone-100 text-stone-800",
    "Consent Pending": "bg-blue-50 text-blue-800",
    Verified:          "bg-blue-50 text-blue-800",
    Approved:          "bg-emerald-50 text-emerald-800",
    Published:         "bg-emerald-100 text-emerald-900",
    Paused:            "bg-amber-50 text-amber-900",
    Withdrawn:         "bg-rose-50 text-rose-800",
};

const ACTIONS = [
    { key: "verify",       icon: CheckCircle2, label: "Verify",       tone: "text-blue-700" },
    { key: "approve",      icon: CheckCircle2, label: "Approve",      tone: "text-emerald-700" },
    { key: "publish",      icon: Send,         label: "Publish",      tone: "text-emerald-800" },
    { key: "homepage-on",  icon: Home,         label: "Homepage on",  tone: "text-stone-800" },
    { key: "homepage-off", icon: Home,         label: "Homepage off", tone: "text-stone-500" },
    { key: "feature",      icon: Sparkles,     label: "Feature",      tone: "text-amber-800" },
    { key: "unfeature",    icon: Sparkles,     label: "Unfeature",    tone: "text-stone-500" },
    { key: "pause",        icon: PauseCircle,  label: "Pause",        tone: "text-amber-800" },
    { key: "withdraw",     icon: XCircle,      label: "Withdraw",     tone: "text-rose-700" },
];

const FEATURE_TAGS = [
    "cards", "expenses", "payments", "subscriptions", "investments",
    "documents", "net-worth", "vault-brief", "money-map", "vault-health", "ask-vault",
];


function AuditDrawer({ tid, onClose }) {
    const [rows, setRows] = useState([]);
    useEffect(() => {
        if (!tid) return;
        api.get(`/admin/testimonials/${tid}/audit`).then(r => setRows(r.data?.audit || []));
    }, [tid]);
    useEffect(() => {
        if (!tid) return;
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [tid, onClose]);
    if (!tid) return null;
    return (
        <div className="fixed inset-0 z-50 bg-stone-900/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4"
             onClick={onClose}
             data-testid="testimonial-audit-drawer">
            <div className="bg-white rounded-t-2xl md:rounded-2xl max-w-xl w-full shadow-2xl overflow-hidden max-h-[85vh] flex flex-col"
                 onClick={e => e.stopPropagation()}>
                <div className="bg-stone-900 text-white px-6 py-4 flex items-start justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-amber-400 mb-1">Audit trail</div>
                        <div className="text-xl font-display">{rows.length} events</div>
                    </div>
                    <button onClick={onClose} className="text-stone-400 hover:text-white"><X size={16} /></button>
                </div>
                <div className="p-6 overflow-y-auto space-y-3">
                    {rows.length === 0 && <div className="text-center text-sm text-stone-400 py-8">No audit events yet.</div>}
                    {rows.map(r => (
                        <div key={r.id} className="border border-stone-100 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                                <div className="text-[13px] font-semibold text-stone-900">{r.action}</div>
                                <div className="text-[10.5px] text-stone-500">{r.at?.slice(0, 19).replace("T", " ")}</div>
                            </div>
                            <div className="text-[11px] text-stone-500 mt-0.5">by {r.actor || "system"}</div>
                            {r.details?.note && <div className="text-[12px] text-stone-700 mt-1.5">{r.details.note}</div>}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}


export default function AdminTestimonials() {
    const [data, setData] = useState({ testimonials: [], summary: {}, meta: {} });
    const [filter, setFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [audit, setAudit] = useState(null);
    const [prodCheck, setProdCheck] = useState(null);

    const load = () => {
        api.get("/admin/testimonials/v2").then(r => setData(r.data)).catch(() => {});
        api.get("/public/testimonials/prod-check").then(r => setProdCheck(r.data)).catch(() => {});
    };
    useEffect(() => { load(); }, []);

    const act = async (tid, action) => {
        try {
            await api.post(`/admin/testimonials/${tid}/action`, { action });
            toast.success(`${action} done`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || `Couldn't ${action}`);
        }
    };

    const toggleTag = async (row, tag) => {
        const next = new Set(row.feature_tags || []);
        if (next.has(tag)) next.delete(tag); else next.add(tag);
        try {
            await api.patch(`/admin/testimonials/${row.id}/associate`, { feature_tags: [...next] });
            toast.success("Tags updated");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't update tags");
        }
    };

    const seedIllustrative = async () => {
        try { await api.post("/admin/testimonials/illustrative-seed", { seed: true }); toast.success("Illustrative rows seeded"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Couldn't seed"); }
    };
    const purgeIllustrative = async () => {
        try { await api.delete("/admin/testimonials/illustrative/purge-all"); toast.success("Purged illustrative rows"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Couldn't purge"); }
    };

    const rows = (data.testimonials || []).filter(r =>
        (!filter || (r.customer_name || "").toLowerCase().includes(filter.toLowerCase())
                 || (r.short_quote || r.quote || "").toLowerCase().includes(filter.toLowerCase()))
        && (!statusFilter || r.verification_status === statusFilter)
    );

    return (
        <div className="max-w-6xl mx-auto p-6 md:p-8" data-testid="admin-testimonials">
            <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold flex items-center gap-1.5">
                        <MessageSquare size={12} /> Admin · Content
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">Testimonials.</h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                        Verify, approve and govern every customer voice VAULT shows publicly. Never rewrite customer wording.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={load} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-stone-300 text-[13px] font-semibold text-stone-700 hover:border-stone-500"
                            data-testid="testimonials-refresh">
                        <RefreshCcw size={12} /> Refresh
                    </button>
                    <button onClick={seedIllustrative} className="text-[12.5px] font-semibold text-stone-500 hover:text-stone-800 px-3 py-1.5 rounded-full border border-stone-300" data-testid="testimonials-seed">
                        + Illustrative
                    </button>
                    <button onClick={purgeIllustrative} className="text-[12.5px] font-semibold text-stone-500 hover:text-rose-800 px-3 py-1.5 rounded-full border border-stone-300" data-testid="testimonials-purge">
                        Purge illustrative
                    </button>
                </div>
            </div>

            {/* Prod check banner */}
            {prodCheck?.blocker && (
                <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 mb-6 flex items-start gap-3"
                     data-testid="testimonials-prod-blocker">
                    <ShieldAlert size={16} className="text-rose-700 shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <div className="text-[13px] font-bold text-rose-900">
                            🚫 Production Testimonial Placeholder Detected · {prodCheck.count} row(s)
                        </div>
                        <div className="text-[11.5px] text-rose-800 mt-1">
                            Illustrative rows are enabled for the public feed. Deployments to production must fail
                            until these are paused, purged, or replaced with real customer testimonials.
                        </div>
                    </div>
                </div>
            )}

            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <StatChip label="Total"          value={data.summary?.total || 0} />
                <StatChip label="Published"      value={data.summary?.by_status?.Published || 0} tone="emerald" />
                <StatChip label="Featured"       value={data.summary?.featured || 0} tone="amber" />
                <StatChip label="Illustrative"   value={data.summary?.illustrative || 0} tone="stone" />
            </div>

            {/* Filters */}
            <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="testimonials-filters">
                <input placeholder="Filter by name or quote…" value={filter} onChange={e => setFilter(e.target.value)}
                       className="px-3 py-1.5 border border-stone-300 rounded-full text-[13px] w-64"
                       data-testid="testimonials-filter" />
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                        className="px-3 py-1.5 border border-stone-300 rounded-full text-[13px]"
                        data-testid="testimonials-status-filter">
                    <option value="">All statuses</option>
                    {(data.meta?.verification_statuses || []).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
            </div>

            <div className="space-y-3">
                {rows.length === 0 && (
                    <div className="text-center text-sm text-stone-400 py-12 border border-dashed border-stone-200 rounded-2xl">
                        No testimonials match.
                    </div>
                )}
                {rows.map(r => (
                    <div key={r.id} className={`bg-white border rounded-2xl overflow-hidden ${r.is_illustrative ? "border-amber-300" : "border-stone-200"}`}
                         data-testid={`testimonial-${r.id}`}>
                        {r.is_illustrative && (
                            <div className="px-4 py-1 bg-amber-100 text-amber-900 text-[10.5px] font-bold tracking-widest uppercase text-center">
                                ⚠ ILLUSTRATIVE TESTIMONIAL — NOT A REAL CUSTOMER
                            </div>
                        )}
                        <div className="p-4">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className={`inline-block text-[10.5px] font-semibold px-2 py-0.5 rounded-full ${STATUS_TONE[r.verification_status] || "bg-stone-100 text-stone-700"}`}>
                                            {r.verification_status || "Submitted"}
                                        </span>
                                        {r.featured && <span className="text-[10.5px] font-semibold text-amber-800">★ Featured</span>}
                                        {r.homepage_enabled && <span className="text-[10.5px] font-semibold text-emerald-800">Home</span>}
                                        <span className="text-[10.5px] text-stone-500">
                                            {r.source || "—"} · {r.display_format || "first_initial"} · {r.publication_permission || "—"}
                                        </span>
                                    </div>
                                    <div className="text-[13.5px] font-semibold text-stone-900 mt-1">
                                        {r.customer_name || "—"}
                                        <span className="text-stone-500 font-normal">
                                            {" "}· {[r.role, r.customer_type, r.country].filter(Boolean).join(" · ")}
                                        </span>
                                        {typeof r.rating === "number" && (
                                            <span className="ml-2 inline-flex items-center gap-0.5 text-amber-600 text-[11px]">
                                                <Star size={10} fill="currentColor" /> {r.rating}/5
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-[13px] text-stone-700 mt-1.5 italic">
                                        &ldquo;{r.short_quote || r.quote}&rdquo;
                                    </p>
                                </div>
                                <button onClick={() => setAudit(r.id)}
                                        className="text-[11px] font-semibold text-stone-500 hover:text-stone-900 px-2 py-1 rounded border border-stone-200 inline-flex items-center gap-1 shrink-0"
                                        data-testid={`testimonial-audit-${r.id}`}>
                                    <ClipboardList size={11} /> Audit
                                </button>
                            </div>
                            {/* Actions */}
                            <div className="mt-3 flex gap-1.5 flex-wrap">
                                {ACTIONS.map(a => {
                                    const Icon = a.icon;
                                    return (
                                        <button key={a.key} onClick={() => act(r.id, a.key)}
                                                className={`text-[10.5px] font-semibold px-2.5 py-1 rounded border border-stone-200 hover:border-stone-500 inline-flex items-center gap-1 ${a.tone}`}
                                                data-testid={`testimonial-action-${a.key}-${r.id}`}>
                                            <Icon size={10} /> {a.label}
                                        </button>
                                    );
                                })}
                            </div>
                            {/* Tags */}
                            <div className="mt-3 pt-3 border-t border-stone-100">
                                <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold mb-1.5">Associate with features</div>
                                <div className="flex gap-1 flex-wrap">
                                    {FEATURE_TAGS.map(tag => {
                                        const on = (r.feature_tags || []).includes(tag);
                                        return (
                                            <button key={tag} onClick={() => toggleTag(r, tag)}
                                                    className={`text-[10.5px] font-semibold px-2 py-0.5 rounded-full border ${on ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 text-stone-600"}`}
                                                    data-testid={`testimonial-tag-${tag}-${r.id}`}>
                                                {tag}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-6 flex items-start gap-2 text-[11px] text-stone-500 max-w-3xl">
                <Info size={12} className="mt-0.5 shrink-0" />
                Admins can edit formatting (display format, priority, photograph, disclosure) but never materially
                rewrite a customer&rsquo;s words. Aggregate ratings and customer counts are shown only when we have
                enough verified data to publish them.
            </div>

            <AuditDrawer tid={audit} onClose={() => setAudit(null)} />
        </div>
    );
}


function StatChip({ label, value, tone = "stone" }) {
    const cls = { stone: "bg-white text-stone-900 border-stone-200",
                  emerald: "bg-emerald-50 text-emerald-900 border-emerald-200",
                  amber:   "bg-amber-50 text-amber-900 border-amber-200",
                }[tone];
    return (
        <div className={`rounded-xl border p-3 ${cls}`}>
            <div className="text-[10.5px] uppercase tracking-widest font-semibold opacity-80">{label}</div>
            <div className="text-2xl font-display tabular-nums mt-1">{value}</div>
        </div>
    );
}
